import React, { useCallback } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  SecondaryButton,
  DangerButton,
  IconButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { withLocalePrefix } from 'shared/utils/translation';
import { Row, TalentMatchingApplication, Person } from 'talent_matching/types';
import { SidePanelHandle } from 'shared/components/side_panel';
import PreviewIcon from '@grnhse/seedling/lib/birch/icons/preview';
import AdvanceButton from 'talent_matching/components/shared/advance_button';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import { useRankingInProgress } from 'talent_matching/contexts/ranking_in_progress_context';
import SidePanelTabs from 'talent_matching/components/side_panel/side_panel_tabs';
import MatchStrengthExplanation from 'talent_matching/components/side_panel/match_strength_explanation';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import FeedbackModal from 'talent_matching/components/modals/leave_feedback_modal';
import AddTagsModal from 'talent_matching/components/modals/add_tags_modal';
import RejectModal from 'candidate_profile_redesign/modals/reject/reject_modal';
import SidePanelHeader from 'talent_matching/components/side_panel/side_panel_header';
import OverrideMatchScoreModal from 'talent_matching/components/modals/override_match_score_modal';
import { Tooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import {
  EllipsisMenuButton,
  ExpandableMenu,
  MenuItem,
  MenuList,
} from '@grnhse/seedling/lib/birch/components/expandable_menu';
import CandidateProfileWrapper from './candidate_profile_wrapper';
import { IconButtonFocusRing } from './shared';
import EmailCandidateModal from 'candidate_profile_redesign/modals/email_candidate_modal';
import TransferJobModal from 'candidate_profile_redesign/modals/transfer_job_modal';
import ResolveFraudReportModalWrapper from './resolve_fraud_report_modal_wrapper';
import MergeModalWrapper from './merge_modal_wrapper';
import { useUpdatePerson } from 'talent_matching/hooks/use_people';
import FraudReportCollapsible from 'talent_matching/components/side_panel/fraud_report_collapsible';
import LinkedinActivitySection from 'talent_matching/components/side_panel/linkedin_activity_section';
import FullFraudReportModal from 'fraud/components/full_fraud_report_modal';
import { useFraudReport } from 'talent_matching/hooks/use_fraud_report';
import DocumentViewerModal from 'talent_matching/components/shared/document_viewer_modal';
import useCandidateSidePanel from 'talent_matching/hooks/useCandidateSidePanel';
import {
  useApplicationViewTracking,
  useTrackApplicationView,
} from './candidate_side_panel_hooks';
import {
  trackUserAction,
  type TalentMatchingAction,
} from 'talent_matching/lib/tracker';
import { useRejectModal } from '../../../application_review/components/reject_modal_context';
import BaseSidePanel, { SidePanelNavigationProps } from './base_side_panel';

const t = withLocalePrefix('talent_matching');

type Props = SidePanelNavigationProps & {
  candidatePrefetchBatch: Row[];
  openCalibrationModal?: () => void;
  calibrationModalOpen: boolean;
  onRejectSuccess: (row: Row, closeModal: () => void) => void;
  onAdvanceSuccess: () => void;
  onMergeSuccess?: () => void;
};

const CandidateSidePanel = React.forwardRef<SidePanelHandle, Props>(
  (
    {
      onClose,
      applicationId,
      onToggle,
      isVisible,
      rowCount,
      currentIndex,
      previousCandidate,
      nextCandidate,
      candidatePrefetchBatch,
      selectCandidate,
      openCalibrationModal,
      calibrationModalOpen,
      onRejectSuccess,
      onAdvanceSuccess,
      onMergeSuccess,
    },
    ref
  ) => {
    const {
      linkedinRscEnabled,
      canCreateTags,
      organizationId,
      userId,
      hiringPlanId,
      paths: { getTagsPath },
    } = useUserContext();
    const {
      paths,
      canAdvance,
      canReject,
      feedbackOnly,
      canViewTalentMatchingAiMatchScore,
      canViewFraudReports,
      canDownloadCandidateData,
      canOverrideMatchScore,
      appReviewSessionId,
      activeSidePanelTab,
      setActiveSidePanelTab,
    } = useTalentMatchingTabContext();
    const { hasCalibrationSetting } = useRankingInProgress();

    const updatePerson = useUpdatePerson() as (
      person: TalentMatchingApplication
    ) => void;

    const { handlePanelRef, application, isLoading } = useCandidateSidePanel({
      applicationId,
      ref,
      prefetchCandidatesBatch: candidatePrefetchBatch,
    });

    useApplicationViewTracking({
      isLoading,
      applicationId: application?.id,
      appReviewSessionId,
    });

    // Snowplow analytics view event — fires whenever the panel shows an
    // application (row click, prev/next navigation, or preselected row).
    useTrackApplicationView({
      isLoading,
      applicationId: application?.id,
      source: 'tm_app_review',
      organizationId,
      userId,
      hiringPlanId,
      appReviewSessionId,
    });

    // Emit an App Review user-action event for the current application.
    const trackAppReviewAction = useCallback(
      (action: TalentMatchingAction, metadata?: Record<string, unknown>) => {
        trackUserAction({
          source: 'tm_app_review',
          organizationId,
          userId,
          hiringPlanId,
          appReviewSessionId,
          category: 'action',
          action,
          applicationId: application?.id,
          candidateId: application?.personId,
          metadata,
        });
      },
      [
        organizationId,
        userId,
        hiringPlanId,
        appReviewSessionId,
        application?.id,
        application?.personId,
      ]
    );

    const handleAdvanceSuccess = useCallback(() => {
      trackAppReviewAction('advance');
      onAdvanceSuccess?.();
    }, [trackAppReviewAction, onAdvanceSuccess]);

    const { data: fraudReport } = useFraudReport(application);

    const {
      rejectIsOpen: rejectModalOpen,
      openReject,
      closeReject: closeRejectModal,
      rejectModalProps,
    } = useRejectModal();

    const [showFeedbackModal, openFeedbackModal, closeFeedbackModal] =
      useModal();
    const [addTagsIsOpen, openAddTagsModal, closeAddTagsModal] = useModal();
    const [showOverrideModal, openOverrideModal, closeOverrideModal] =
      useModal();
    const [isSendEmailModalOpen, openSendEmailModal, closeSendEmailModal] =
      useModal();
    const [
      isTransferJobModalOpen,
      openTransferJobModal,
      closeTransferJobModal,
    ] = useModal();
    const [showFraudModal, openFraudModal, closeFraudModal] = useModal();

    const openRejectModal = useCallback(() => {
      openReject();
    }, [openReject]);

    const openResolveModalRef = React.useRef<(() => void) | null>(null);
    const openMergeModalRef = React.useRef<(() => void) | null>(null);

    const profileUrl = application?.personId
      ? `/people/${application.personId}/applications/${application.id}`
      : null;

    const goToCandidateProfile = () => {
      if (!profileUrl) {
        return;
      }
      window.open(profileUrl, '_blank');
      trackAppReviewAction('navigate_to_profile');
    };

    const externalDisableClickOutside =
      showFeedbackModal ||
      rejectModalOpen ||
      addTagsIsOpen ||
      calibrationModalOpen ||
      showOverrideModal ||
      showFraudModal ||
      isSendEmailModalOpen ||
      isTransferJobModalOpen;

    const externalModalsOpen =
      showFeedbackModal ||
      rejectModalOpen ||
      addTagsIsOpen ||
      calibrationModalOpen ||
      showOverrideModal ||
      showFraudModal;

    const fraudReportCollapsible = () => (
      <FraudReportCollapsible
        application={application}
        openFraudModal={openFraudModal}
        openRejectModal={() => {
          openReject({
            rejectionCategory: 'security_risk',
          });
        }}
        canReject={canReject}
        onResolveFraudReport={() => openResolveModalRef.current?.()}
      />
    );

    const matchStrengthExplanation = () => (
      <MatchStrengthExplanation
        application={application}
        openCalibrationModal={openCalibrationModal}
        openOverrideModal={
          canOverrideMatchScore ? openOverrideModal : undefined
        }
        showExplanationKey="talent_matching/match_strength_explanation"
        hasCalibrationSetting={hasCalibrationSetting}
        onAiSummaryExpand={() =>
          trackUserAction({
            source: 'tm_app_review',
            organizationId,
            userId,
            hiringPlanId,
            category: 'interaction',
            action: 'ai_summary_click',
            applicationId: application?.id,
            candidateId: application?.personId,
          })
        }
      />
    );

    const collapsibleComponents = () => {
      const components = [];

      if (canViewTalentMatchingAiMatchScore) {
        components.push(matchStrengthExplanation());
      }

      if (canViewFraudReports) {
        if (application.fraudReportHighRisk) {
          components.unshift(fraudReportCollapsible());
        } else {
          components.push(fraudReportCollapsible());
        }
      }

      return components;
    };

    return (
      <>
        <BaseSidePanel
          handlePanelRef={handlePanelRef}
          application={application as TalentMatchingApplication}
          isLoading={isLoading}
          onClose={onClose}
          onToggle={onToggle}
          isVisible={isVisible}
          rowCount={rowCount}
          currentIndex={currentIndex}
          previousCandidate={previousCandidate}
          nextCandidate={nextCandidate}
          selectCandidate={selectCandidate}
          applicationId={applicationId}
          activeSidePanelTab={activeSidePanelTab}
          setActiveSidePanelTab={setActiveSidePanelTab}
          canDownloadCandidateData={canDownloadCandidateData}
          externalDisableClickOutside={externalDisableClickOutside}
          externalModalsOpen={externalModalsOpen}
          renderHeader={({ application: app }) => (
            <SidePanelHeader
              application={app as Person}
              onDuplicateClick={
                app.potentialDuplicate
                  ? () => openMergeModalRef.current?.()
                  : undefined
              }
            />
          )}
        >
          {({
            application: app,
            isLoading: loading,
            setExtendedResumePreview,
            setLinkedinPreview,
            setModalDocument,
            extendedResumePreview,
            modalDocument,
            allowExtendedPreview,
          }) => (
            <>
              <Stack
                direction="row"
                spacing={1}
                sx={{ paddingTop: 1, paddingBottom: 2 }}
              >
                {!feedbackOnly && (
                  <>
                    <Tooltip
                      label={
                        canAdvance ? '' : t('table.tooltips.cannot_advance')
                      }
                      ariaLabel={t('table.tooltips.cannot_advance')}
                    >
                      <AdvanceButton
                        application={app as Person}
                        canAdvance={canAdvance}
                        onSuccess={handleAdvanceSuccess}
                      />
                    </Tooltip>
                    <Tooltip
                      label={canReject ? '' : t('table.tooltips.cannot_reject')}
                      ariaLabel={t('table.tooltips.cannot_reject')}
                    >
                      <DangerButton
                        onClick={() => openRejectModal()}
                        size="md"
                        disabled={!canReject}
                      >
                        {t('reject')}
                      </DangerButton>
                    </Tooltip>
                  </>
                )}
                <SecondaryButton onClick={openFeedbackModal} size="md">
                  {app?.userScorecard
                    ? t('edit_feedback')
                    : t('leave_feedback')}
                </SecondaryButton>
                <SecondaryButton onClick={openAddTagsModal} size="md">
                  {t('tag')}
                </SecondaryButton>
                {!app.anonymizeResumes && (
                  <IconButtonFocusRing>
                    <IconButton
                      icon={PreviewIcon}
                      label={t('go_to_candidate_profile')}
                      onClick={goToCandidateProfile}
                      size="md"
                      disabled={!profileUrl}
                      focusable
                    />
                  </IconButtonFocusRing>
                )}
                {/* @ts-expect-error - TS2322 */}
                <ExpandableMenu dangerouslyBypassScrollLock>
                  <EllipsisMenuButton aria-label={t('table.labels.actions')} />
                  <MenuList>
                    <MenuItem onSelect={openTransferJobModal}>
                      {t('transfer_to_another_job')}
                    </MenuItem>
                    {app?.email && (
                      <MenuItem onSelect={openSendEmailModal}>
                        {t('send_email')}
                      </MenuItem>
                    )}
                  </MenuList>
                </ExpandableMenu>
              </Stack>
              {linkedinRscEnabled && (
                <LinkedinActivitySection
                  linkedinAddress={app.linkedinAddress}
                  onViewLinkedin={
                    app.linkedinIframePath
                      ? () => {
                          setLinkedinPreview(
                            app.linkedinIframePath as string | null
                          );
                        }
                      : undefined
                  }
                />
              )}
              {collapsibleComponents().map((collapsible) => collapsible)}
              {!loading && (
                <SidePanelTabs
                  application={app as Person}
                  activeDocument={
                    extendedResumePreview ?? modalDocument ?? null
                  }
                  onOpenDocument={
                    allowExtendedPreview
                      ? (doc) => {
                          setModalDocument(null);
                          setLinkedinPreview(null);
                          setExtendedResumePreview(doc);
                        }
                      : (doc) => {
                          setExtendedResumePreview(null);
                          setLinkedinPreview(null);
                          setModalDocument(doc);
                        }
                  }
                  openFeedbackModal={openFeedbackModal}
                  canDownloadCandidateData={canDownloadCandidateData}
                />
              )}
              <RejectModal
                bulkMode={false}
                closeRejectModal={closeRejectModal}
                isRejectModalOpen={rejectModalOpen}
                getRejectModalPath={app.rejectModalPath as string}
                candidateName={app.fullName}
                changeApplicationStatePath={app.rejectPath}
                rejectionTrackingPayload={{
                  app_review_session_id: appReviewSessionId,
                  source: 'talent_matching',
                  action_source: 'side_panel',
                }}
                onRejectSuccess={() => {
                  trackAppReviewAction('reject');
                  onRejectSuccess(app, closeRejectModal);
                  setModalDocument(null);
                  if (extendedResumePreview) {
                    setExtendedResumePreview(null);
                  }
                }}
                {...rejectModalProps}
              />
              {modalDocument && (
                <DocumentViewerModal
                  document={modalDocument}
                  onClose={() => setModalDocument(null)}
                  openFeedbackModal={openFeedbackModal}
                  openRejectModal={openRejectModal}
                  application={app as Person}
                  canAdvance={canAdvance}
                  canReject={canReject}
                  onAdvanceSuccess={handleAdvanceSuccess}
                  canDownloadCandidateData={canDownloadCandidateData}
                />
              )}
            </>
          )}
        </BaseSidePanel>

        {application && (
          <>
            <FeedbackModal
              isOpen={showFeedbackModal}
              closeModal={closeFeedbackModal}
              application={application}
              hiringPlanTagsPath={paths.hiringPlanTagsPath}
              onSuccess={() => trackAppReviewAction('leave_feedback')}
            />
            <AddTagsModal
              isOpen={addTagsIsOpen}
              closeModal={closeAddTagsModal}
              getTagsPath={getTagsPath}
              addTagsPath={application.addTagsPath}
              fullName={application.fullName}
              currentTags={application.tags}
              canCreateTags={canCreateTags}
              onSuccess={(tags) => {
                trackAppReviewAction('tag', { tag_count: tags.length });
                updatePerson({
                  ...application,
                  tags,
                } as TalentMatchingApplication);
              }}
            />
            <OverrideMatchScoreModal
              isOpen={showOverrideModal}
              onClose={closeOverrideModal}
              application={application as TalentMatchingApplication}
              updatePerson={updatePerson}
              onSuccess={(matchStrength) =>
                trackAppReviewAction('override_match_strength', {
                  match_strength: matchStrength,
                })
              }
            />
            <CandidateProfileWrapper
              candidateProfileContextPath={
                application.candidateProfileContextPath
              }
            >
              <ResolveFraudReportModalWrapper
                fraudReport={fraudReport ?? null}
                applicationId={application.id}
                onModalOpenReady={(openFn) => {
                  openResolveModalRef.current = openFn;
                }}
              />
              <MergeModalWrapper
                onModalOpenReady={(openFn) => {
                  openMergeModalRef.current = openFn;
                }}
                onMergeSuccess={onMergeSuccess}
              />
              <TransferJobModal
                isOpen={isTransferJobModalOpen}
                onDismiss={closeTransferJobModal}
                hiringPlansPath={application.transferModalHiringPlansPath}
                ownerOptionsPath={application.transferModalOwnerOptionsPath}
                submitPath={application.addPersonToJobPath}
                onSuccess={() => trackAppReviewAction('transfer_to_job')}
              />
              <EmailCandidateModal
                isOpen={isSendEmailModalOpen}
                handleClose={closeSendEmailModal}
                modalPath={application.emailCandidatePath}
                title={t('send_email_to', { name: application.fullName })}
                onSubmit={() => trackAppReviewAction('send_email')}
              />
            </CandidateProfileWrapper>
            {fraudReport && (
              <FullFraudReportModal
                candidateName={application.fullName}
                report={fraudReport}
                isOpen={showFraudModal}
                closeModal={closeFraudModal}
              />
            )}
          </>
        )}
      </>
    );
  }
);

CandidateSidePanel.displayName = 'CandidateSidePanel';

export default CandidateSidePanel;
